import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getUnserviceableAreaRequests, deleteUnserviceableAreaRequest } from '../../actions/unserviceableAreaRequest';
import { 
    MapPin, 
    Search, 
    Trash2, 
    Eye, 
    User, 
    Calendar, 
    Navigation,
    Globe,
    AlertTriangle,
    RefreshCw,
    Map
} from 'lucide-react';

const UnserviceableAreaRequests = ({ 
    getUnserviceableAreaRequests, 
    deleteUnserviceableAreaRequest, 
    unserviceableAreaRequests, 
    pagination,
    loading 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [requestToDelete, setRequestToDelete] = useState(null);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);

    useEffect(() => {
        getUnserviceableAreaRequests(currentPage, limit);
    }, [currentPage]);

    useEffect(() => {
        if (unserviceableAreaRequests) {
            const filtered = unserviceableAreaRequests.filter(request =>
                request.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request.location?.coordinates?.some(coord => 
                    coord.toString().includes(searchTerm)
                )
            );
            setFilteredRequests(filtered);
        }
    }, [unserviceableAreaRequests, searchTerm]);

    const handleDeleteClick = (request) => {
        setRequestToDelete(request);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (requestToDelete) {
            deleteUnserviceableAreaRequest(requestToDelete._id);
            setShowDeleteModal(false);
            setRequestToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setRequestToDelete(null);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRefresh = () => {
        getUnserviceableAreaRequests(currentPage, limit);
    };

    const openInGoogleMaps = (coordinates) => {
        const googleMapsUrl = process.env.REACT_APP_GOOGLE_MAPS_URL || 'https://www.google.com/maps';
        const latitude = coordinates[0];
        const longitude = coordinates[1];
        const mapsUrl = `${googleMapsUrl}?q=${latitude},${longitude}`;
        window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <MapPin size={32} className="me-3 text-primary" />
                                    Unserviceable Area Requests
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage location requests for areas not currently serviceable
                                </p>
                            </div>
                            <div className="d-flex gap-2">
                                <button 
                                    onClick={handleRefresh}
                                    className="btn btn-outline-primary btn-lg shadow-sm d-flex align-items-center"
                                    disabled={loading}
                                >
                                    <RefreshCw size={20} className="me-2" />
                                    {loading ? 'Refreshing...' : 'Refresh'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="row mb-4">
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{pagination?.total || 0}</div>
                                        <div className="small">Total Requests</div>
                                    </div>
                                    <MapPin size={28} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{pagination?.pages || 0}</div>
                                        <div className="small">Total Pages</div>
                                    </div>
                                    <Navigation size={28} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{pagination?.page || 1}</div>
                                        <div className="small">Current Page</div>
                                    </div>
                                    <Calendar size={28} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
                        <div className="card shadow-sm border-0 bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="h4 mb-0">{filteredRequests?.length || 0}</div>
                                        <div className="small">Showing</div>
                                    </div>
                                    <Eye size={28} className="opacity-75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search Section */}
                <div className="row mb-4">
                    <div className="col-lg-8">
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <Search size={18} className="text-muted" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search by user name, request ID, or coordinates..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setSearchTerm('')}
                                            title="Clear search"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Requests Table */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading unserviceable area requests...</p>
                    </div>
                ) : filteredRequests && filteredRequests.length > 0 ? (
                    <div className="card shadow-sm">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <MapPin size={16} className="me-2 text-muted" />
                                                    Request ID
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <User size={16} className="me-2 text-muted" />
                                                    User
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <Navigation size={16} className="me-2 text-muted" />
                                                    Location
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <Calendar size={16} className="me-2 text-muted" />
                                                    Created
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <AlertTriangle size={16} className="me-2 text-muted" />
                                                    Status
                                                </div>
                                            </th>
                                            <th scope="col" className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRequests.map((request) => (
                                            <tr key={request._id} className="align-middle">
                                                <td className="text-center">
                                                    <div className="fw-bold text-primary">
                                                        #{request._id.slice(-6)}
                                                    </div>
                                                    <div className="small text-muted">
                                                        {request._id}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="fw-bold">
                                                        {request.user?.name || 'Unknown User'}
                                                    </div>
                                                    {request.user?._id && (
                                                        <div className="small text-muted">
                                                            ID: {request.user._id.slice(-6)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {request.location?.coordinates ? (
                                                        <div>
                                                            <div className="fw-bold small">
                                                                {request.location.coordinates[0]}, {request.location.coordinates[1]}
                                                            </div>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary mt-1"
                                                                onClick={() => openInGoogleMaps(request.location.coordinates)}
                                                                style={{ fontSize: '0.7rem' }}
                                                            >
                                                                <Globe size={12} className="me-1" />
                                                                View Map
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">No location</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="fw-bold">
                                                        {new Date(request.createdAt).toLocaleDateString()}
                                                    </div>
                                                    <div className="small text-muted">
                                                        {new Date(request.createdAt).toLocaleTimeString()}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25">
                                                        Unserviceable Area
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="btn-group" role="group">
                                                        <Link 
                                                            to={`/inventory/v1.2/unserviceable-area-requests/${request._id}`}
                                                            className="btn btn-sm btn-outline-primary"
                                                            title="View Details"
                                                        >
                                                            <Eye size={14} />
                                                        </Link>
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteClick(request)}
                                                            title="Delete Request"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <MapPin size={64} className="text-muted mb-3" />
                        <h4 className="text-muted mb-3">No Unserviceable Area Requests Found</h4>
                        <p className="text-muted mb-4">
                            {searchTerm ? 'No requests match your search criteria.' : 'No unserviceable area requests have been submitted yet.'}
                        </p>
                        {searchTerm && (
                            <button 
                                className="btn btn-outline-primary"
                                onClick={() => setSearchTerm('')}
                            >
                                Clear Search
                            </button>
                        )}
                    </div>
                )}

                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="d-flex justify-content-center">
                                <nav aria-label="Unserviceable Area Requests pagination">
                                    <ul className="pagination">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(1)}
                                                disabled={currentPage === 1}
                                            >
                                                ««
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                ‹
                                            </button>
                                        </li>
                                        
                                        {/* Page numbers */}
                                        {(() => {
                                            const pageNumbers = [];
                                            const startPage = Math.max(1, currentPage - 2);
                                            const endPage = Math.min(pagination.pages, currentPage + 2);
                                            
                                            for (let i = startPage; i <= endPage; i++) {
                                                pageNumbers.push(
                                                    <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => handlePageChange(i)}
                                                        >
                                                            {i}
                                                        </button>
                                                    </li>
                                                );
                                            }
                                            return pageNumbers;
                                        })()}
                                        
                                        <li className={`page-item ${currentPage === pagination.pages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === pagination.pages}
                                            >
                                                ›
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === pagination.pages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(pagination.pages)}
                                                disabled={currentPage === pagination.pages}
                                            >
                                                »»
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div 
                    className="modal fade show" 
                    style={{ 
                        display: 'block',
                        zIndex: 1050,
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        overflow: 'auto',
                        background: 'rgba(0,0,0,0.5)'
                    }} 
                    tabIndex="-1"
                >
                    <div className="modal-dialog modal-dialog-centered" style={{ pointerEvents: 'auto' }}>
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title text-danger">
                                    <AlertTriangle size={20} className="me-2" />
                                    Confirm Delete
                                </h5>
                                <button type="button" className="btn-close" onClick={cancelDelete}></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this unserviceable area request?</p>
                                <p className="text-muted small mb-0">This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer border-0">
                                <button type="button" className="btn btn-secondary" onClick={cancelDelete}>
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger" 
                                    onClick={confirmDelete}
                                    style={{ position: 'relative', zIndex: 1055 }}
                                >
                                    <Trash2 size={16} className="me-2" />
                                    Delete Request
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

UnserviceableAreaRequests.propTypes = {
    getUnserviceableAreaRequests: PropTypes.func.isRequired,
    deleteUnserviceableAreaRequest: PropTypes.func.isRequired,
    unserviceableAreaRequests: PropTypes.array,
    pagination: PropTypes.object,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    unserviceableAreaRequests: state.unserviceableAreaRequest.unserviceableAreaRequests,
    pagination: state.unserviceableAreaRequest.pagination,
    loading: state.unserviceableAreaRequest.loading
});

export default connect(mapStateToProps, { getUnserviceableAreaRequests, deleteUnserviceableAreaRequest })(UnserviceableAreaRequests);
